/**
 * useFlightSchoolFlow — PWA port of the Flight School flow state machine.
 *
 * Source of truth: client-native/src/features/flight-school/hooks/useFlightSchoolFlow.ts
 * (DO NOT edit the iOS file).
 *
 * Linear index over `module.activities` with a back-stack, an offset progress
 * bar (never empty on activity 1), flashcard result capture, and module-complete
 * side-effects: marks the module complete, sets placement A0 + completes the
 * `placement_test` tutorial item on `cognates-1`, and advances the activation
 * step when the current step gates on completed lessons.
 *
 * PostHog analytics are wired to match iOS (module started/step/milestone/
 * pattern/flashcard/completed/graduated/abandoned). Native asset preload has no
 * PWA equivalent. Navigation away from FS is delegated to the caller via `onExit`.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { callWithAuth, postPlacementResult, getLessonData } from "@/api/Api";
import { LESSON_DATA_QUERY_KEY } from "@/query_keys";
import { useActivationJourney } from "@/features/activation";
import { emitCompletedChapterSignal } from "@/components/Course/completedChapterSignal";
import {
  MODULE_1,
  MODULE_2,
  MODULE_3,
  MODULE_4,
  CION_WORDS,
  ANTE_WORDS,
  DAD_WORDS,
  MENTE_WORDS,
  ALL_COGNATE_WORDS,
  BASIC_PHRASES_1_WORDS,
} from "../lib/moduleData";
import { ActivityData, CognateWord, Module } from "../lib/activityTypes";
import { useFlightSchoolCourse } from "./useFlightSchoolCourse";
import { useFlightSchoolConfig } from "./useFlightSchoolConfig";
import { completeTutorialItem } from "../lib/flightSchoolApi";
import { preloadFlightSchoolActivityAssets } from "../lib/activityAssets";
import { useTrackFlightSchoolEvents } from "@/hooks/posthog/useTrackFlightSchoolEvents";

// Hardcoded fallback maps (source of truth content)
const FALLBACK_MODULE_MAP: Record<string, Module> = {
  "cognates-1": MODULE_1,
  "cognates-2": MODULE_2,
  "cognates-3": MODULE_3,
  "basic-phrases-1": MODULE_4,
};

const FALLBACK_WORDS_MAP: Record<string, CognateWord[]> = {
  "cognates-1": [...CION_WORDS, ...ANTE_WORDS],
  "cognates-2": [...DAD_WORDS, ...MENTE_WORDS],
  "cognates-3": [...ALL_COGNATE_WORDS],
  "basic-phrases-1": [...BASIC_PHRASES_1_WORDS],
};

const FALLBACK_MODULE_IDS = Object.keys(FALLBACK_MODULE_MAP);

interface UseFlightSchoolFlowProps {
  /** Which module to run (defaults to cognates-1). */
  moduleId?: string;
  /** Optional entrypoint for dev/testing flows. */
  initialActivityId?: string;
  /** Called when the flow wants to leave Flight School (back past start, or
   *  module complete). The PWA wires this to react-router navigation. */
  onExit?: () => void;
}

interface UseFlightSchoolFlowReturn {
  currentActivity: ActivityData | null;
  progress: number;
  totalActivities: number;
  currentIndex: number;
  handleActivityComplete: (
    completedActivityId?: string,
    knownWordIds?: string[],
    learningWordIds?: string[],
  ) => void;
  handleBack: () => void;
  canGoBack: boolean;
  isComplete: boolean;
  flashcardKnownWordIds: string[];
  flashcardLearningWordIds: string[];
  allWords: CognateWord[];
  currentModuleId: string;
}

function shouldCompleteCurrentActivity(
  currentActivity: { id: string } | null | undefined,
  completedActivityId?: string,
): boolean {
  return (
    completedActivityId === undefined ||
    currentActivity?.id === completedActivityId
  );
}

export function useFlightSchoolFlow(
  props?: UseFlightSchoolFlowProps,
): UseFlightSchoolFlowReturn {
  const moduleId = props?.moduleId || "cognates-1";
  const initialActivityId = props?.initialActivityId;
  const onExit = props?.onExit;
  const { moduleMap, modules: configModules } = useFlightSchoolConfig();
  const queryClient = useQueryClient();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [activityHistory, setActivityHistory] = useState<number[]>([]);
  const [flashcardKnownWordIds, setFlashcardKnownWordIds] = useState<string[]>(
    [],
  );
  const [flashcardLearningWordIds, setFlashcardLearningWordIds] = useState<
    string[]
  >([]);
  const { completeModule, completedModules } = useFlightSchoolCourse();
  const {
    trackModuleStarted,
    trackStepCompleted,
    trackPatternUnlocked,
    trackFlashcardCompleted,
    trackModuleCompleted,
    trackFlightSchoolGraduated,
    trackAbandoned,
    resetMilestones,
  } = useTrackFlightSchoolEvents();
  const {
    stepConfig: activationStepConfig,
    isActivationComplete,
    advanceStep,
    stats,
  } = useActivationJourney();

  const moduleCompletedRef = useRef(false);
  const appliedInitialActivityRef = useRef<string | null>(null);

  // Get the module from backend config or fallback to hardcoded
  const module = moduleMap[moduleId] || FALLBACK_MODULE_MAP[moduleId] || MODULE_1;

  const allWords = useMemo(() => {
    const configModule = moduleMap[moduleId];
    if (configModule?.words) {
      return Object.values(configModule.words).flat() as CognateWord[];
    }
    return FALLBACK_WORDS_MAP[moduleId] || [...CION_WORDS, ...ANTE_WORDS];
  }, [moduleId, moduleMap]);

  const totalActivities = module.activities.length;

  const availableModuleCount =
    configModules.length > 0
      ? new Set([
          ...configModules.map((m) => m.id),
          ...FALLBACK_MODULE_IDS,
        ]).size
      : FALLBACK_MODULE_IDS.length;

  // Track module started on mount, and abandon on unmount if not completed
  // (iOS useFlightSchoolFlow.ts:154-175). Deps are [moduleId] only — moduleId
  // won't change during a session, matching iOS's mount/unmount semantics.
  useEffect(() => {
    moduleCompletedRef.current = false;
    resetMilestones();
    trackModuleStarted(moduleId);

    return () => {
      if (!moduleCompletedRef.current) {
        const activity = module.activities[currentIndex];
        const progressPercent =
          Math.round(((currentIndex + 1) / totalActivities) * 100 * 100) / 100;
        trackAbandoned(
          moduleId,
          currentIndex,
          activity?.type || "unknown",
          progressPercent,
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const currentActivity = useMemo(() => {
    if (currentIndex >= module.activities.length) {
      return null;
    }
    return module.activities[currentIndex];
  }, [currentIndex, module.activities]);

  // Preload the next activity's assets on every index change (iOS
  // useFlightSchoolFlow.ts:184-186). Warm MCQ option images and, for upcoming
  // video activities, prefetch the lesson query so the player skips the
  // "Loading video…" spinner.
  useEffect(() => {
    const next = module.activities[currentIndex + 1];
    if (!next) return;
    preloadFlightSchoolActivityAssets(next);
    if (next.type === "video") {
      const lessonId = next.lessonId;
      void queryClient.prefetchQuery({
        queryKey: [LESSON_DATA_QUERY_KEY + lessonId],
        queryFn: () => callWithAuth(getLessonData, { lessonId }),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [currentIndex, module.activities, queryClient]);

  // Start with a small offset so the bar isn't empty on the first activity.
  const progress = useMemo(() => {
    return (currentIndex + 1) / (totalActivities + 1);
  }, [currentIndex, totalActivities]);

  const isComplete = currentIndex >= totalActivities;

  useEffect(() => {
    if (!initialActivityId) return;
    if (appliedInitialActivityRef.current === initialActivityId) return;

    const initialIndex = module.activities.findIndex(
      (activity) => activity.id === initialActivityId,
    );
    if (initialIndex === -1) {
      appliedInitialActivityRef.current = initialActivityId;
      return;
    }

    setActivityHistory([]);
    setCurrentIndex(initialIndex);
    appliedInitialActivityRef.current = initialActivityId;
  }, [initialActivityId, module.activities]);

  const handleActivityComplete = useCallback(
    async (
      completedActivityId?: string,
      knownWordIds?: string[],
      learningWordIds?: string[],
    ) => {
      const activity = module.activities[currentIndex];
      if (!shouldCompleteCurrentActivity(activity, completedActivityId)) {
        return;
      }

      // Track the step completion (+ milestone detection inside the hook).
      if (activity) {
        trackStepCompleted(
          moduleId,
          currentIndex,
          activity.type,
          totalActivities,
        );
      }

      // Track pattern unlock if applicable.
      if (activity?.type === "pattern_unlocked" && "pattern" in activity) {
        trackPatternUnlocked(activity.pattern);
      }

      // Capture flashcard results for the recap + track completion.
      if (activity?.type === "flashcard" && knownWordIds && learningWordIds) {
        setFlashcardKnownWordIds(knownWordIds);
        setFlashcardLearningWordIds(learningWordIds);
        trackFlashcardCompleted(
          moduleId,
          knownWordIds.length,
          learningWordIds.length,
          knownWordIds.length + learningWordIds.length,
        );
      }

      // Final activity: pattern_power, or celebrate with isFinal.
      const isFinalCelebrate =
        activity?.type === "celebrate" &&
        "isFinal" in activity &&
        activity.isFinal;
      const isFinalActivity =
        activity?.type === "pattern_power" || isFinalCelebrate;

      if (isFinalActivity) {
        // Mark module as completed for tracking (prevents the abandon event).
        moduleCompletedRef.current = true;

        // Track module completion (iOS useFlightSchoolFlow.ts:262-266).
        trackModuleCompleted(
          moduleId,
          flashcardKnownWordIds.length,
          flashcardLearningWordIds.length,
        );

        // Graduating = the completion that reaches the available-module count
        // (iOS parity — :269-275). availableModuleCount already merges config.
        const updatedCompletedCount = completedModules.includes(moduleId)
          ? completedModules.length
          : completedModules.length + 1;
        const hasGraduated = updatedCompletedCount >= availableModuleCount;
        if (hasGraduated) {
          trackFlightSchoolGraduated(updatedCompletedCount);
        }

        if (!completedModules.includes(moduleId)) {
          // iOS optimistically increments completedLessonsCount via
          // addCompletedFlightSchoolModule() BEFORE this check, so finishing
          // the module that reaches the goal advances immediately. The web
          // stats snapshot excludes the just-finished module, so compare against
          // `stats.completedLessons + 1` for this first-time completion.
          if (
            !isActivationComplete &&
            activationStepConfig?.check === "completedLessons" &&
            stats.completedLessons + 1 >= activationStepConfig.goal
          ) {
            void advanceStep();
          }
        }

        // iOS useFlightSchoolFlow.ts:288 — hand the completion off to the Home
        // path. On graduation the FS home path shows CourseCompletedPopup,
        // whose CTA switches the current course to the TikTok course
        // (HomeScreen.tsx:399-403) — the flow itself no longer auto-switches.
        emitCompletedChapterSignal({
          chapterId: moduleId,
          title: "Flight School",
          isFlightSchool: true,
          courseCompleted: hasGraduated,
          isFirstCompletion: true,
        });

        onExit?.();

        try {
          if (moduleId === "cognates-1") {
            await callWithAuth(postPlacementResult, { placementResult: "A0" });
            await callWithAuth(completeTutorialItem, "placement_test");
          }
          await completeModule(moduleId);
        } catch (error) {
          console.error("[FlightSchool] Failed to save progress:", error);
        }

        return;
      }

      const nextIndex = currentIndex + 1;

      if (nextIndex >= totalActivities) {
        onExit?.();
      } else {
        setActivityHistory((prev) => [...prev, currentIndex]);
        setCurrentIndex(nextIndex);
      }
    },
    [
      currentIndex,
      totalActivities,
      module.activities,
      moduleId,
      completeModule,
      completedModules,
      flashcardKnownWordIds,
      flashcardLearningWordIds,
      trackStepCompleted,
      trackPatternUnlocked,
      trackFlashcardCompleted,
      trackModuleCompleted,
      trackFlightSchoolGraduated,
      activationStepConfig,
      isActivationComplete,
      advanceStep,
      stats.completedLessons,
      availableModuleCount,
      onExit,
    ],
  );

  const handleBack = useCallback(() => {
    if (activityHistory.length > 0) {
      const previousIndex = activityHistory[activityHistory.length - 1];
      setActivityHistory((prev) => prev.slice(0, -1));
      setCurrentIndex(previousIndex);
    } else {
      onExit?.();
    }
  }, [activityHistory, onExit]);

  const canGoBack = activityHistory.length > 0 || currentIndex === 0;

  return {
    currentActivity,
    progress,
    totalActivities,
    currentIndex,
    handleActivityComplete,
    handleBack,
    canGoBack,
    isComplete,
    flashcardKnownWordIds,
    flashcardLearningWordIds,
    allWords,
    currentModuleId: moduleId,
  };
}
